import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import axios from "../../../config/axiosConfig";
import Alert from "@mui/material/Alert";

// Estado 5 (Entregado) de la OT Activar Equipos Vendidos: espejo simplificado de
// concluyendo.php. A diferencia de ConcluirVentaOT.jsx, acá el precio SÍ es editable —
// esta OT no tiene un paso previo que ya lo haya capturado (el equipo llega con el
// precio_venta de cuando se vendió, en otra OT, o ninguno), así que se prellena con el
// precio sugerido del contrato vigente y el técnico lo puede ajustar antes de concluir.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.1, px: 0.6, fontSize: 10.5, lineHeight: 1.3 } };

const ConcluirActivarVendidosOT = ({ cod_ot, cod_servicio_cliente, esTKR, esAVL, onConcluido }) => {
  const [items, setItems] = useState([]);
  const [observacion, setObservacion] = useState("");
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    axios.get(`/api/ot-activar-vendidos/activados/${cod_ot}`)
      .then(async (res) => {
        const rows = res.data || [];
        const conPrecio = await Promise.all(
          rows.map(async (r) => {
            try {
              const { data } = await axios.get("/api/ot-activar-vendidos/precio-sugerido", {
                params: { cod_servicio_cliente, familia: r.familia },
              });
              return { ...r, precioVenta: Number(data?.precio || 0) };
            } catch {
              return { ...r, precioVenta: 0 };
            }
          })
        );
        setItems(conPrecio);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [cod_ot, cod_servicio_cliente]);

  const setPrecio = (key, valor) => {
    setItems((prev) => prev.map((r) => (`${r.familia}-${r.identificador}` === key ? { ...r, precioVenta: valor } : r)));
  };

  const concluir = async () => {
    if (!window.confirm("¿Concluir esta OT con los precios indicados?")) return;
    setGuardando(true);
    setError("");
    try {
      const payload = {
        items: items.map((r) => ({
          familia: r.familia,
          identificador: r.identificador,
          precioVenta: Number(r.precioVenta) || 0,
        })),
        observacion,
      };
      const res = await axios.patch(`/api/ot-activar-vendidos/finalizar-con-precio/${cod_ot}`, payload);
      if (onConcluido) onConcluido(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo concluir la OT.");
    } finally {
      setGuardando(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={20} /></Box>;
  }

  const titulo = esTKR ? "Radios y baterías activados" : esAVL ? "Módems y chips activados" : "Equipos activados";

  return (
    <Box sx={{ mt: 1 }}>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        {titulo}
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 320, borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Identificador</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
              <TableCell sx={{ ...headCellSx, width: 110 }} align="right">Precio</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!items.length ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No hay equipos activados en esta OT.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((r) => {
                const key = `${r.familia}-${r.identificador}`;
                return (
                  <TableRow key={key} hover>
                    <TableCell>{r.identificador}</TableCell>
                    <TableCell>{r.modelo}</TableCell>
                    <TableCell align="right">
                      <TextField
                        variant="standard"
                        size="small"
                        type="number"
                        value={r.precioVenta ?? ""}
                        onChange={(e) => setPrecio(key, e.target.value)}
                        sx={{ width: 90, "& .MuiInputBase-input": { fontSize: 11, textAlign: "right" } }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TextField
        label="Observación de Conclusión"
        fullWidth
        multiline
        rows={3}
        value={observacion}
        onChange={(e) => setObservacion(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" color="success" onClick={concluir} disabled={guardando}>
          {guardando ? <CircularProgress size={20} color="inherit" /> : "Concluir"}
        </Button>
      </Box>
    </Box>
  );
};

export default ConcluirActivarVendidosOT;
